import React, { useMemo, useState } from 'react';
import xlsx from 'json-as-xlsx';
import { Colors, ChartColors } from '../../../constants/Colors.js';
import env from '../../../environment.js';
import EmptyStateChart from '../empty-state-chart.js';

const ESTADOS = ['alto', 'bajo', 'nulo'];

/**
 * Tabla del tablero: busca, ordena, filtra por nivel de uso y exporta a Excel.
 * También es la lectura alterna de las gráficas, para quien no distingue los
 * colores de las series.
 *
 * columnas: [{ key, label, tipo: 'texto' | 'numero' | 'estado' }]
 */
const UsoSirioTabla = ({ columnas, filas, nombreArchivo, hoja = 'Uso de Sirio', conFiltroEstado = false }) => {
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('todos');
  const [orden, setOrden] = useState({ key: columnas[0].key, asc: true });

  const visibles = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    let resultado = filas;

    if (termino) {
      resultado = resultado.filter(fila =>
        String(fila.cooperativa || '')
          .toLowerCase()
          .includes(termino)
      );
    }
    if (conFiltroEstado && estado !== 'todos') {
      resultado = resultado.filter(fila => fila.uso_sirio === estado);
    }

    const columna = columnas.find(col => col.key === orden.key);
    return [...resultado].sort((a, b) => {
      const va = a[orden.key];
      const vb = b[orden.key];
      const comparacion = columna?.tipo === 'numero' ? (va || 0) - (vb || 0) : String(va ?? '').localeCompare(String(vb ?? ''), 'es');
      return orden.asc ? comparacion : -comparacion;
    });
  }, [filas, busqueda, estado, orden, columnas, conFiltroEstado]);

  const ordenarPor = key => {
    setOrden(actual => (actual.key === key ? { key, asc: !actual.asc } : { key, asc: key === 'cooperativa' }));
  };

  const exportar = () => {
    xlsx(
      [
        {
          sheet: hoja,
          columns: columnas.map(col => ({ label: col.label, value: col.key })),
          content: visibles,
        },
      ],
      { fileName: nombreArchivo }
    );
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 10 }}>
        <input
          type="text"
          className="form-control select-ciclo"
          style={{ maxWidth: 240 }}
          placeholder="Buscar cooperativa…"
          value={busqueda}
          onChange={event => setBusqueda(event.target.value)}
        />

        {conFiltroEstado && (
          <div className="btn-group" role="group">
            {['todos', ...ESTADOS].map(opcion => (
              <button
                key={opcion}
                type="button"
                className="btn btn-light text-capitalize"
                style={{
                  borderColor: Colors.neutral_100,
                  backgroundColor: estado === opcion ? Colors.neutral_3 : 'transparent',
                  fontSize: '0.8rem',
                }}
                onClick={() => setEstado(opcion)}>
                {opcion}
              </button>
            ))}
          </div>
        )}

        <button onClick={exportar} className="btn ml-auto" type="button" title="Descargar Excel">
          <img src={env.images.icon.xls} className="pr-1" alt="" />
          <span style={{ color: Colors.primary_500, fontSize: '0.76rem' }}>Descargar Excel</span>
        </button>
      </div>

      {visibles.length === 0 ? (
        <EmptyStateChart message="Ninguna cooperativa coincide con el filtro" />
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
          <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                {columnas.map(columna => (
                  <th
                    key={columna.key}
                    onClick={() => ordenarPor(columna.key)}
                    className="clickable"
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: Colors.white,
                      borderTop: 'none',
                      borderBottom: `1px solid ${Colors.neutral_100}`,
                      color: Colors.gray_base,
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      textAlign: columna.tipo === 'numero' ? 'right' : 'left',
                    }}>
                    {columna.label}
                    {orden.key === columna.key && <span className="ml-1">{orden.asc ? '▲' : '▼'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.map((fila, i) => (
                <tr key={fila.id_cooperativa ?? i}>
                  {columnas.map(columna => (
                    <td
                      key={columna.key}
                      style={{
                        textAlign: columna.tipo === 'numero' ? 'right' : 'left',
                        whiteSpace: 'nowrap',
                        color: columna.tipo === 'numero' && !fila[columna.key] ? Colors.neutral_200 : Colors.neutral_800,
                      }}>
                      {columna.tipo === 'estado' ? (
                        <span
                          className="text-capitalize"
                          style={{
                            backgroundColor: ChartColors.estadoSuave[fila[columna.key]],
                            color: ChartColors.estado[fila[columna.key]],
                            borderRadius: 20,
                            padding: '2px 10px',
                            fontSize: 12,
                            fontWeight: 600,
                          }}>
                          {fila[columna.key]}
                        </span>
                      ) : (
                        fila[columna.key]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsoSirioTabla;
